import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

// project-imports
import useAuth from 'hooks/useAuth';
import { useNotifications } from 'hooks/useNotifications';
import { useGetMenuMaster } from 'api/menu';
import { clacUiGlobal as ui } from 'utils/uiGlobal';
import { PermissionGate } from 'components/PermissionGate';
import { HELP_URL, SUPPORT_TICKET_URL } from 'config';
import { openSnackbar } from 'api/snackbar';
import { SnackbarProps } from 'types/snackbar';

// assets
import { Book1, Logout, Notification as BellIcon, Profile, Ticket } from 'iconsax-reactjs';

function getAvatarSrc(foto: string | null | undefined): string | undefined {
  if (!foto) return undefined;
  return foto;
}

// ==============================|| LIST - USER ||============================== //

const getInitials = (name?: string | null) => {
  const safe = (name ?? '').trim();
  if (!safe) return 'U';
  const parts = safe.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return `${first}${last}`.toUpperCase();
};

export default function UserList() {
  const intl = useIntl();
  const navigate = useNavigate();

  const { logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const userAnchorRef = useRef<HTMLDivElement | null>(null);
  const [userOpen, setUserOpen] = useState(false);

  const initials = useMemo(() => getInitials(user?.name), [user?.name]);
  const roleLabel = useMemo(() => {
    const role = Array.isArray(user?.role) ? user?.role[0] : user?.role;
    return role ? String(role) : '';
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const openExternal = (url: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isValidHttpUrl = (value: string): boolean => {
    if (!value) return false;
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSupportClick = () => {
    if (!isValidHttpUrl(SUPPORT_TICKET_URL)) {
      openSnackbar({
        open: true,
        message: intl.formatMessage({ id: 'layout.sidebar.support-ticket.config-error' }),
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'error' },
        close: true
      } as SnackbarProps);
      return;
    }
    openExternal(SUPPORT_TICKET_URL);
  };

  const sidebarActionSx = {
    display: 'flex',
    alignItems: 'center',
    gap: drawerOpen ? '9px' : 0,
    justifyContent: drawerOpen ? 'flex-start' : 'center',
    padding: drawerOpen ? '8px 10px' : '11px 0',
    borderRadius: ui.radius.sm,
    fontSize: ui.fonts.sizes.sm,
    fontWeight: ui.fonts.weights.medium,
    color: ui.semantic.text.secondary,
    cursor: 'pointer',
    userSelect: 'none' as const,
    transition: 'background .12s',
    '&:hover': { backgroundColor: ui.semantic.bg.page }
  };

  return (
    <Box sx={{ p: 1.5, borderTop: '1px solid', borderTopColor: ui.semantic.border.subtle, display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Tooltip title={intl.formatMessage({ id: 'layout.sidebar.help' })} placement="right" disableHoverListener={drawerOpen}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={intl.formatMessage({ id: 'layout.sidebar.help' })}
        onClick={() => openExternal(HELP_URL)}
        sx={sidebarActionSx}
      >
        <Book1 size={16} style={{ flexShrink: 0 }} />
        {drawerOpen && (
        <Typography sx={{ fontSize: ui.fonts.sizes.sm, fontWeight: ui.fonts.weights.medium, color: ui.semantic.text.secondary, whiteSpace: 'nowrap' }}>
          <FormattedMessage id="layout.sidebar.help" />
        </Typography>
        )}
      </Box>
      </Tooltip>

      <Tooltip title={intl.formatMessage({ id: 'layout.sidebar.support-ticket' })} placement="right" disableHoverListener={drawerOpen}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={intl.formatMessage({ id: 'layout.sidebar.support-ticket' })}
        onClick={handleSupportClick}
        sx={sidebarActionSx}
      >
        <Ticket size={16} style={{ flexShrink: 0 }} />
        {drawerOpen && (
        <Typography sx={{ fontSize: ui.fonts.sizes.sm, fontWeight: ui.fonts.weights.medium, color: ui.semantic.text.secondary, whiteSpace: 'nowrap' }}>
          <FormattedMessage id="layout.sidebar.support-ticket" />
        </Typography>
        )}
      </Box>
      </Tooltip>

      <Divider sx={{ my: 0.25, borderColor: ui.semantic.border.subtle }} />

      <PermissionGate role={['Administrador', 'Manager', 'Colaborador']}>
      <Tooltip
        title={
          unreadCount > 0
            ? `${intl.formatMessage({ id: 'notifications' })} · ${unreadCount}`
            : intl.formatMessage({ id: 'notifications' })
        }
        placement="right"
        disableHoverListener={drawerOpen}
      >
      <Box
        aria-label={intl.formatMessage({ id: 'notifications' })}
        component={Link}
        to="/notificaciones"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: drawerOpen ? '9px' : 0,
          justifyContent: drawerOpen ? 'flex-start' : 'center',
          padding: drawerOpen ? '8px 10px' : '11px 0',
          borderRadius: ui.radius.sm,
          fontSize: ui.fonts.sizes.sm,
          fontWeight: ui.fonts.weights.medium,
          color: ui.semantic.text.secondary,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background .12s',
          textDecoration: 'none',
          position: 'relative',
          '&:hover': { backgroundColor: ui.semantic.bg.page }
        }}
      >
        <BellIcon size={16} style={{ flexShrink: 0 }} />
        {drawerOpen && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: ui.fonts.sizes.sm, fontWeight: ui.fonts.weights.medium, color: ui.semantic.text.secondary, whiteSpace: 'nowrap' }}>
              <FormattedMessage id="notifications" />
            </Typography>
          </Box>
        )}
        {unreadCount > 0 &&
          (drawerOpen ? (
            <Box
              component="span"
              sx={{
                backgroundColor: ui.semantic.accent.main,
                color: ui.colors.white,
                fontSize: ui.fonts.sizes['4xs'],
                fontWeight: ui.fonts.weights.bold,
                borderRadius: ui.radius.pill,
                padding: '1px 6px',
                lineHeight: 1.5,
                flexShrink: 0
              }}
            >
              {unreadCount}
            </Box>
          ) : (
            <Box
              component="span"
              aria-hidden
              sx={{
                position: 'absolute',
                top: 6,
                left: '50%',
                ml: '4px',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: ui.semantic.accent.main,
                border: `2px solid ${ui.semantic.bg.surface}`
              }}
            />
          ))}
      </Box>
      </Tooltip>
      </PermissionGate>

      <Box
        ref={userAnchorRef}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={userOpen ? 'true' : 'false'}
        onClick={() => setUserOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setUserOpen((prev) => !prev);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: drawerOpen ? 1 : 0,
          justifyContent: drawerOpen ? 'flex-start' : 'center',
          padding: drawerOpen ? '8px 10px' : '8px 0',
          borderRadius: ui.radius.sm,
          cursor: 'pointer',
          transition: 'background .12s',
          position: 'relative',
          '&:hover': { backgroundColor: ui.semantic.bg.page }
        }}
      >
        <Avatar
          alt={user?.name ?? 'User'}
          src={getAvatarSrc(user?.foto)}
          sx={{
            width: 28,
            height: 28,
            bgcolor: ui.semantic.accent.bg,
            border: `1.5px solid ${ui.semantic.accent.border}`,
            color: ui.semantic.accent.text,
            fontSize: '10px',
            fontWeight: 700,
            flexShrink: 0
          }}
        >
          {initials}
        </Avatar>
        {drawerOpen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: ui.fonts.sizes.xs, fontWeight: ui.fonts.weights.semibold, color: ui.semantic.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user?.name ?? intl.formatMessage({ id: 'layout.user.unknown' })}
          </Typography>
          <Typography sx={{ fontSize: ui.fonts.sizes['2xs'], color: ui.semantic.text.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {roleLabel}
          </Typography>
        </Box>
        )}
      </Box>

      <Popper open={userOpen} anchorEl={userAnchorRef.current} placement="top-start" disablePortal>
        <ClickAwayListener onClickAway={() => setUserOpen(false)}>
          <Paper
            role="menu"
            sx={{
              width: 240,
              borderRadius: ui.radius.md,
              border: `1px solid ${ui.semantic.border.subtle}`,
              boxShadow: ui.shadow.raised,
              p: 0.5
            }}
          >
            <Stack sx={{ gap: 0.25 }}>
              <Box
                component={Link}
                to="/apps/profiles/user/personal"
                onClick={() => setUserOpen(false)}
                role="menuitem"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: ui.radius.sm,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: ui.semantic.text.secondary,
                  textDecoration: 'none',
                  '&:hover': { backgroundColor: ui.semantic.bg.page, color: ui.semantic.text.primary }
                }}
              >
                <Profile size={14} />
                <FormattedMessage id="layout.user.profile" />
              </Box>
              <Divider sx={{ my: 0.25 }} />
              <Box
                component="button"
                type="button"
                onClick={handleLogout}
                role="menuitem"
                style={{ width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    borderRadius: ui.radius.sm,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: ui.semantic.danger.text,
                    '&:hover': { backgroundColor: ui.semantic.danger.bg }
                  }}
                >
                  <Logout size={14} />
                  <FormattedMessage id="layout.user.logout" />
                </Box>
              </Box>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
